import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import inscricoesService from "../../../services/inscricoesService";
import type {
  Inscricao,
  ListarInscricoesParams,
} from "../../../services/inscricoesService";

const STATUS_COLORS = {
  pendente: "bg-yellow-100 text-yellow-800",
  em_analise: "bg-blue-100 text-blue-800",
  aprovado: "bg-green-100 text-green-800",
  reprovado: "bg-red-100 text-red-800",
};

const STATUS_LABELS = {
  pendente: "Pendente",
  em_analise: "Em Análise",
  aprovado: "Aprovado",
  reprovado: "Reprovado",
};

export default function ListaInscricoes() {
  const [inscricoes, setInscricoes] = useState<Inscricao[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filtros
  const [filtroStatus, setFiltroStatus] = useState<string>("");
  const [filtroEdital, setFiltroEdital] = useState<string>("");
  const [filtroBusca, setFiltroBusca] = useState<string>("");

  // Paginação
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [totalPaginas, setTotalPaginas] = useState(1);
  const [totalRegistros, setTotalRegistros] = useState(0);

  // Estatísticas
  const [estatisticas, setEstatisticas] = useState({
    total: 0,
    por_status: {
      pendentes: 0,
      em_analise: 0,
      aprovadas: 0,
      reprovadas: 0,
    },
  });

  const carregarInscricoes = async () => {
    try {
      setLoading(true);
      setError(null);

      const params: ListarInscricoesParams = {
        pagina: paginaAtual,
        por_pagina: 20,
      };

      if (filtroStatus)
        params.status = filtroStatus as
          | "pendente"
          | "em_analise"
          | "aprovado"
          | "reprovado";
      if (filtroEdital) params.edital_id = parseInt(filtroEdital);
      if (filtroBusca) params.busca_proponente = filtroBusca;

      const response = await inscricoesService.listar(params);

      setInscricoes(response.inscricoes);
      setPaginaAtual(response.paginacao.pagina_atual);
      setTotalPaginas(response.paginacao.total_paginas);
      setTotalRegistros(response.paginacao.total_registros);
      setEstatisticas(response.estatisticas);
    } catch (err) {
      setError("Erro ao carregar inscrições");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void carregarInscricoes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginaAtual, filtroStatus, filtroEdital]);

  const handleBuscar = (e: React.FormEvent) => {
    e.preventDefault();
    setPaginaAtual(1);
    carregarInscricoes();
  };

  const limparFiltros = () => {
    setFiltroStatus("");
    setFiltroEdital("");
    setFiltroBusca("");
    setPaginaAtual(1);
  };

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">
          Análise de Inscrições
        </h1>
        <p className="mt-2 text-gray-600">
          Gerencie e analise as inscrições submetidas aos editais
        </p>
      </div>

      {/* Estatísticas */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm font-medium text-gray-600">Total</div>
          <div className="mt-1 text-2xl font-bold text-gray-900">
            {estatisticas.total}
          </div>
        </div>
        <div className="bg-yellow-50 rounded-lg shadow p-4">
          <div className="text-sm font-medium text-yellow-600">Pendentes</div>
          <div className="mt-1 text-2xl font-bold text-yellow-900">
            {estatisticas.por_status.pendentes}
          </div>
        </div>
        <div className="bg-blue-50 rounded-lg shadow p-4">
          <div className="text-sm font-medium text-blue-600">Em Análise</div>
          <div className="mt-1 text-2xl font-bold text-blue-900">
            {estatisticas.por_status.em_analise}
          </div>
        </div>
        <div className="bg-green-50 rounded-lg shadow p-4">
          <div className="text-sm font-medium text-green-600">Aprovadas</div>
          <div className="mt-1 text-2xl font-bold text-green-900">
            {estatisticas.por_status.aprovadas}
          </div>
        </div>
        <div className="bg-red-50 rounded-lg shadow p-4">
          <div className="text-sm font-medium text-red-600">Reprovadas</div>
          <div className="mt-1 text-2xl font-bold text-red-900">
            {estatisticas.por_status.reprovadas}
          </div>
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <form onSubmit={handleBuscar} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Status */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                value={filtroStatus}
                onChange={(e) => setFiltroStatus(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Todos</option>
                <option value="pendente">Pendente</option>
                <option value="em_analise">Em Análise</option>
                <option value="aprovado">Aprovado</option>
                <option value="reprovado">Reprovado</option>
              </select>
            </div>

            {/* Busca por Proponente */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Buscar Proponente
              </label>
              <input
                type="text"
                value={filtroBusca}
                onChange={(e) => setFiltroBusca(e.target.value)}
                placeholder="Nome ou email..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Botões */}
            <div className="flex items-end gap-2">
              <button
                type="submit"
                className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition"
              >
                Buscar
              </button>
              <button
                type="button"
                onClick={limparFiltros}
                className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition"
              >
                Limpar
              </button>
            </div>
          </div>
        </form>
      </div>

      {/* Loading */}
      {loading && (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">Carregando inscrições...</p>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Lista de Inscrições */}
      {!loading && !error && (
        <>
          {inscricoes.length === 0 ? (
            <div className="bg-gray-50 rounded-lg p-12 text-center">
              <p className="text-gray-600">
                Nenhuma inscrição encontrada com os filtros selecionados.
              </p>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Proponente
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Edital
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Data
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {inscricoes.map((inscricao) => (
                    <tr key={inscricao.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        #{inscricao.id}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {inscricao.proponente_nome}
                        </div>
                        <div className="text-sm text-gray-500">
                          {inscricao.proponente_email}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900 max-w-xs truncate">
                          {inscricao.edital_nome}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            STATUS_COLORS[inscricao.status]
                          }`}
                        >
                          {STATUS_LABELS[inscricao.status]}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(inscricao.created_at).toLocaleDateString(
                          "pt-BR"
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <Link
                          to={`/admin/inscricoes/${inscricao.id}`}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          Analisar
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Paginação */}
          {totalPaginas > 1 && (
            <div className="mt-6 flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Mostrando{" "}
                <span className="font-medium">{inscricoes.length}</span> de{" "}
                <span className="font-medium">{totalRegistros}</span> inscrições
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setPaginaAtual(Math.max(1, paginaAtual - 1))}
                  disabled={paginaAtual === 1}
                  className="px-4 py-2 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  Anterior
                </button>
                <span className="px-4 py-2 text-gray-700">
                  Página {paginaAtual} de {totalPaginas}
                </span>
                <button
                  onClick={() =>
                    setPaginaAtual(Math.min(totalPaginas, paginaAtual + 1))
                  }
                  disabled={paginaAtual === totalPaginas}
                  className="px-4 py-2 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  Próxima
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
